import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  MoreVertical,
  Eye,
  Send,
  Copy,
  Trash2,
  Calculator,
  PenLine,
  Clock,
  CheckCircle2,
  X,
  Tag,
  Download,
  ChevronRight
} from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { auth } from '../firebase';
import { getDisplayName } from '../lib/utils';
import { useRole } from '../context/RoleContext';
import { useAllQuotes } from '../features/quotes/useQuotes';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

/**
 * Quotes Page - Global view of all quotes
 * 
 * Mobile-first responsive design:
 * - Card layout on mobile (<768px)
 * - Table layout on desktop (≥768px)
 */

const formatCurrency = (value) => {
  if (typeof value !== 'number') return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

const formatDate = (timestamp) => {
  if (!timestamp) return '—';
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : 
                 timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
};

const tagToDisplayName = (tag) => {
  if (!tag) return '';
  return tag
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getTagColor = (tag) => {
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-green-100', text: 'text-green-700' },
    { bg: 'bg-purple-100', text: 'text-purple-700' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },
    { bg: 'bg-pink-100', text: 'text-pink-700' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  ];
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getStatusBadge = (status, compact = false) => {
  const baseClasses = compact 
    ? 'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium'
    : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium';
    
  switch (status) {
    case 'sent':
      return (
        <span className={`${baseClasses} bg-green-100 text-green-700`}>
          <CheckCircle2 className="w-3 h-3" />
          {!compact && 'Sent'}
        </span>
      );
    case 'booked':
      return (
        <span className={`${baseClasses} bg-blue-100 text-blue-700`}>
          <CheckCircle2 className="w-3 h-3" />
          {!compact && 'Booked'}
        </span>
      );
    case 'draft':
    default:
      return (
        <span className={`${baseClasses} bg-gray-100 text-gray-600`}>
          <Clock className="w-3 h-3" />
          {!compact && 'Draft'}
        </span>
      );
  }
};

const getSourceIcon = (source, size = 'w-4 h-4') => {
  if (source === 'calculator') {
    return <Calculator className={`${size} text-blue-500`} title="Calculator" />;
  }
  return <PenLine className={`${size} text-purple-500`} title="Manual" />;
};

const ServiceBadges = ({ tags, maxVisible = 2, onTagClick, compact = false }) => {
  if (!tags || tags.length === 0) return <span className="text-gray-400 text-sm">—</span>;
  
  const visibleTags = tags.slice(0, maxVisible);
  const hiddenCount = tags.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleTags.map((tag) => {
        const color = getTagColor(tag);
        return (
          <button
            key={tag}
            onClick={(e) => {
              e.stopPropagation();
              onTagClick?.(tag);
            }}
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${color.bg} ${color.text} hover:opacity-80 transition-opacity`}
          >
            {tagToDisplayName(tag)}
          </button>
        );
      })}
      {hiddenCount > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};

/**
 * Mobile Quote Card Component
 */
const QuoteCard = ({ quote, onView, onDownloadPdf, onTagClick, companyId }) => {
  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 active:bg-gray-50 transition-colors"
      onClick={() => onView(quote)}
      data-testid={`quote-card-${quote.quoteId}`}
    >
      {/* Top row: Client name + Total */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">
            {getDisplayName(quote.client, 'No Client')}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-bold text-gray-900">
            {formatCurrency(quote.totals?.grandTotal)}
          </div>
        </div>
      </div>

      {/* Services row */}
      <div className="mb-2">
        <ServiceBadges tags={quote.tags} maxVisible={3} onTagClick={onTagClick} compact />
      </div>

      {/* Bottom row: Status, Source, Date, Action */}
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {getStatusBadge(quote.status, true)}
          {getSourceIcon(quote.source || 'manual', 'w-3.5 h-3.5')}
          <span className="text-xs text-gray-500">{formatDate(quote.createdAt)}</span>
        </div>
        <div className="flex items-center gap-1">
          {quote.pdfUrl && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDownloadPdf(quote);
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Download PDF"
            >
              <Download className="w-4 h-4 text-gray-500" />
            </button>
          )}
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

const Quotes = () => {
  const navigate = useNavigate();
  const { companyId } = useCompany();
  const { role } = useRole();
  
  const { quotes, loading } = useAllQuotes({ companyId });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState(null);

  const canDelete = role === 'owner' || role === 'admin';

  const allTags = useMemo(() => {
    const tagSet = new Set();
    quotes.forEach((quote) => {
      (quote.tags || []).forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [quotes]);

  const filteredQuotes = quotes.filter((quote) => {
    if (statusFilter !== 'all' && quote.status !== statusFilter) return false;
    if (tagFilter && !(quote.tags || []).includes(tagFilter)) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const name = getDisplayName(quote.client, '').toLowerCase();
      const address = formatAddress(quote.client?.address).toLowerCase();
      const tagMatch = (quote.tags || []).some(tag => 
        tagToDisplayName(tag).toLowerCase().includes(search)
      );
      return name.includes(search) || address.includes(search) || tagMatch;
    }
    return true;
  });

  const formatAddress = (address) => {
    if (!address) return '—';
    if (typeof address === 'string') return address;
    const parts = [];
    if (address.line1) parts.push(address.line1);
    if (address.city) parts.push(address.city);
    if (address.state) parts.push(address.state);
    return parts.join(', ') || '—';
  };

  const handleView = (quote) => {
    navigate(`/quotes/${quote.quoteId}?companyId=${companyId}`);
  };

  const handleSend = async (quote) => {
    toast.info('Send functionality coming soon');
  };

  const handleDuplicate = async (quote) => {
    toast.info('Duplicate functionality coming soon');
  };

  const handleDelete = async (quote) => {
    if (!canDelete) {
      toast.error('Only admins can delete quotes');
      return;
    }
    toast.info('Delete functionality coming soon');
  };

  const handleDownloadPdf = async (quote) => {
    if (!quote.pdfUrl) {
      toast.info('Generating PDF...');
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const token = await auth.currentUser?.getIdToken(false);
        const response = await fetch(`${backendUrl}/api/quotes/pdf/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ companyId, quoteId: quote.quoteId }),
        });
        const data = await response.json();
        if (data.success && data.pdfUrl) {
          window.open(data.pdfUrl, '_blank');
          toast.success('PDF generated');
        } else {
          toast.error('Failed to generate PDF');
        }
      } catch (err) {
        toast.error('Failed to generate PDF');
      }
      return;
    }
    window.open(quote.pdfUrl, '_blank');
  };

  const handleTagClick = (tag) => {
    setTagFilter(tagFilter === tag ? null : tag);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTagFilter(null);
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || tagFilter;

  return (
    <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[rgba(242,107,29,0.1)] flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-[var(--cb-orange)]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-bold text-secondary truncate">Quotes</h1>
            <p className="text-xs md:text-sm text-muted-foreground hidden sm:block">
              Manage and track all your quotes
            </p>
          </div>
        </div>

        <Button
          onClick={() => navigate('/quotes/new')}
          className="text-white shrink-0"
          style={{ backgroundColor: 'var(--cb-orange)' }}
          size="sm"
          data-testid="create-quote-btn"
        >
          <Plus className="w-4 h-4 md:mr-2" />
          <span className="hidden md:inline">New Quote</span>
        </Button>
      </div>

      {/* Filters - Responsive */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10"
            data-testid="search-quotes-input"
          />
        </div>

        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[110px] sm:w-[130px] h-10" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="booked">Booked</SelectItem>
            </SelectContent>
          </Select>

          <Select value={tagFilter || 'all'} onValueChange={(v) => setTagFilter(v === 'all' ? null : v)}>
            <SelectTrigger className="w-[110px] sm:w-[150px] h-10" data-testid="service-filter">
              <Tag className="w-4 h-4 mr-1 sm:mr-2 shrink-0" />
              <SelectValue placeholder="Service" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              {allTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tagToDisplayName(tag)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearFilters}
              className="h-10 w-10 shrink-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Active Tag Filter Badge */}
      {tagFilter && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Filtered:</span>
          <button
            onClick={() => setTagFilter(null)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tagFilter).bg} ${getTagColor(tagFilter).text}`}
          >
            {tagToDisplayName(tagFilter)}
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--cb-orange)] mx-auto mb-4" />
          <p className="text-gray-500">Loading quotes...</p>
        </div>
      ) : filteredQuotes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {hasActiveFilters ? 'No quotes found' : 'No quotes yet'}
          </h3>
          <p className="text-sm text-gray-500 mb-6">
            {hasActiveFilters
              ? 'Try adjusting your search or filters'
              : 'Create your first quote to get started'}
          </p>
          {!hasActiveFilters && (
            <Button
              onClick={() => navigate('/quotes/new')}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Quote
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Card Layout */}
          <div className="md:hidden space-y-3">
            {filteredQuotes.map((quote) => (
              <QuoteCard
                key={quote.quoteId}
                quote={quote}
                onView={handleView}
                onDownloadPdf={handleDownloadPdf}
                onTagClick={handleTagClick}
                companyId={companyId}
              />
            ))}
          </div>

          {/* Desktop: Table Layout */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredQuotes.map((quote) => (
                  <tr
                    key={quote.quoteId}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleView(quote)}
                    data-testid={`quote-row-${quote.quoteId}`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">
                          {getDisplayName(quote.client, 'No Client')}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-[200px]">
                          {formatAddress(quote.client?.address)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <ServiceBadges 
                        tags={quote.tags} 
                        maxVisible={2} 
                        onTagClick={handleTagClick}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(quote.totals?.grandTotal)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(quote.status)}
                    </td>
                    <td className="px-6 py-4">
                      {getSourceIcon(quote.source || 'manual')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(quote.createdAt)}
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 hover:bg-gray-100 rounded-lg" data-testid={`quote-actions-${quote.quoteId}`}>
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(quote)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownloadPdf(quote)}>
                            <Download className="w-4 h-4 mr-2" />
                            {quote.pdfUrl ? 'Download PDF' : 'Generate PDF'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSend(quote)}>
                            <Send className="w-4 h-4 mr-2" />
                            Send
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(quote)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(quote)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Summary */}
      {!loading && filteredQuotes.length > 0 && (
        <div className="text-sm text-gray-500 text-right">
          Showing {filteredQuotes.length} of {quotes.length} quotes
        </div>
      )}
    </div>
  );
};

export default Quotes;
